"use client"
import { useState } from "react"
import { InlineMath } from "react-katex"
import "katex/dist/katex.min.css"
export default function AddQuestionMathsExpression({ onClose, onInsert, fieldType }) {
  const [activeCategory, setActiveCategory] = useState("Basic")
  const [showInputDialog, setShowInputDialog] = useState(false)
  const [currentMathType, setCurrentMathType] = useState(null)
  const [inputValues, setInputValues] = useState({})
  const [previewLatex, setPreviewLatex] = useState("")
  const [showTrigPowerDialog, setShowTrigPowerDialog] = useState(false)
  const [trigPowerData, setTrigPowerData] = useState({ func: "", inputName: "", power: "1", argument: "" })
  // Math categories with smart input requirements
  const mathCategories = {
    Basic: [
      { symbol: "+", latex: "+", label: "Plus", type: "direct" },
      { symbol: "-", latex: "-", label: "Minus", type: "direct" },
      { symbol: "×", latex: "\\times", label: "Multiply", type: "direct" },
      { symbol: "÷", latex: "\\div", label: "Divide", type: "direct" },
      { symbol: "=", latex: "=", label: "Equals", type: "direct" },
      { symbol: "≠", latex: "\\neq", label: "Not Equal", type: "direct" },
      { symbol: "<", latex: "<", label: "Less Than", type: "direct" },
      { symbol: ">", latex: ">", label: "Greater Than", type: "direct" },
      { symbol: "≤", latex: "\\leq", label: "Less Equal", type: "direct" },
      { symbol: "≥", latex: "\\geq", label: "Greater Equal", type: "direct" },
    ],
    Powers: [
      { symbol: "x²", latex: "x^2", label: "Square", type: "power", inputs: ["base"] },
      { symbol: "x³", latex: "x^3", label: "Cube", type: "power", inputs: ["base"] },
      { symbol: "xⁿ", latex: "x^n", label: "Power", type: "power", inputs: ["base", "exponent"] },
      { symbol: "√x", latex: "\\sqrt{x}", label: "Square Root", type: "root", inputs: ["value"] },
      { symbol: "∛x", latex: "\\sqrt[3]{x}", label: "Cube Root", type: "root", inputs: ["value"] },
      { symbol: "ⁿ√x", latex: "\\sqrt[n]{x}", label: "Nth Root", type: "root", inputs: ["index", "value"] },
    ],
    Fractions: [
      { symbol: "½", latex: "\\frac{1}{2}", label: "Half", type: "direct" },
      {
        symbol: "a/b",
        latex: "\\frac{a}{b}",
        label: "Fraction",
        type: "fraction",
        inputs: ["numerator", "denominator"],
      },
      {
        symbol: "a/b²",
        latex: "\\frac{a}{b^2}",
        label: "Fraction with Power",
        type: "fraction_power",
        inputs: ["numerator", "denominator", "power"],
      },
    ],
    Calculus: [
      { symbol: "∫", latex: "\\int", label: "Integral", type: "integral", inputs: ["function"] },
      {
        symbol: "∫ᵃᵇ",
        latex: "\\int_{a}^{b}",
        label: "Definite Integral",
        type: "definite_integral",
        inputs: ["lower", "upper", "function"],
      },
      { symbol: "d/dx", latex: "\\frac{d}{dx}", label: "Derivative", type: "derivative", inputs: ["function"] },
      {
        symbol: "∂/∂x",
        latex: "\\frac{\\partial}{\\partial x}",
        label: "Partial Derivative",
        type: "partial",
        inputs: ["function", "variable"],
      },
      { symbol: "∑", latex: "\\sum", label: "Sum", type: "sum", inputs: ["expression"] },
      {
        symbol: "∑ᵢ",
        latex: "\\sum_{i=1}^{n}",
        label: "Sum with limits",
        type: "sum_limits",
        inputs: ["variable", "lower", "upper", "expression"],
      },
      { symbol: "∏", latex: "\\prod", label: "Product", type: "product", inputs: ["expression"] },
      {
        symbol: "lim",
        latex: "\\lim_{x \\to a}",
        label: "Limit",
        type: "limit",
        inputs: ["variable", "approaches", "function"],
      },
    ],
    Greek: [
      { symbol: "α", latex: "\\alpha", label: "Alpha", type: "direct" },
      { symbol: "β", latex: "\\beta", label: "Beta", type: "direct" },
      { symbol: "γ", latex: "\\gamma", label: "Gamma", type: "direct" },
      { symbol: "δ", latex: "\\delta", label: "Delta", type: "direct" },
      { symbol: "θ", latex: "\\theta", label: "Theta", type: "direct" },
      { symbol: "π", latex: "\\pi", label: "Pi", type: "direct" },
      { symbol: "λ", latex: "\\lambda", label: "Lambda", type: "direct" },
      { symbol: "μ", latex: "\\mu", label: "Mu", type: "direct" },
      { symbol: "σ", latex: "\\sigma", label: "Sigma", type: "direct" },
      { symbol: "Ω", latex: "\\Omega", label: "Omega", type: "direct" },
    ],
    Trig: [
      { symbol: "sin", latex: "\\sin", label: "Sine", type: "function", inputs: ["argument"] },
      { symbol: "cos", latex: "\\cos", label: "Cosine", type: "function", inputs: ["argument"] },
      { symbol: "tan", latex: "\\tan", label: "Tangent", type: "function", inputs: ["argument"] },
      {
        symbol: "sinⁿ",
        latex: "\\sin",
        label: "Sine to Power n",
        type: "trig_power_n",
        inputs: ["power", "argument"],
      },
      {
        symbol: "cosⁿ",
        latex: "\\cos",
        label: "Cosine to Power n",
        type: "trig_power_n",
        inputs: ["power", "argument"],
      },
      {
        symbol: "tanⁿ",
        latex: "\\tan",
        label: "Tangent to Power n",
        type: "trig_power_n",
        inputs: ["power", "argument"],
      },
      { symbol: "sec", latex: "\\sec", label: "Secant", type: "function", inputs: ["argument"] },
      { symbol: "csc", latex: "\\csc", label: "Cosecant", type: "function", inputs: ["argument"] },
      { symbol: "cot", latex: "\\cot", label: "Cotangent", type: "function", inputs: ["argument"] },
      {
        symbol: "secⁿ",
        latex: "\\sec",
        label: "Secant to Power n",
        type: "trig_power_n",
        inputs: ["power", "argument"],
      },
      {
        symbol: "cscⁿ",
        latex: "\\csc",
        label: "Cosecant to Power n",
        type: "trig_power_n",
        inputs: ["power", "argument"],
      },
      {
        symbol: "cotⁿ",
        latex: "\\cot",
        label: "Cotangent to Power n",
        type: "trig_power_n",
        inputs: ["power", "argument"],
      },
      { symbol: "log", latex: "\\log", label: "Logarithm", type: "function", inputs: ["argument"] },
      { symbol: "ln", latex: "\\ln", label: "Natural Log", type: "function", inputs: ["argument"] },
      {
        symbol: "logⁿ",
        latex: "\\log",
        label: "Log to Power n",
        type: "trig_power_n",
        inputs: ["power", "argument"],
      },
      {
        symbol: "lnⁿ",
        latex: "\\ln",
        label: "Natural Log to Power n",
        type: "trig_power_n",
        inputs: ["power", "argument"],
      },
      { symbol: "e", latex: "e", label: "Euler's number", type: "direct" },
    ],
    Sets: [
      { symbol: "∈", latex: "\\in", label: "Element Of", type: "direct" },
      { symbol: "∉", latex: "\\notin", label: "Not Element Of", type: "direct" },
      { symbol: "⊂", latex: "\\subset", label: "Subset", type: "direct" },
      { symbol: "∪", latex: "\\cup", label: "Union", type: "direct" },
      { symbol: "∩", latex: "\\cap", label: "Intersection", type: "direct" },
      { symbol: "∞", latex: "\\infty", label: "Infinity", type: "direct" },
      {
        symbol: "sinⁿ",
        latex: "\\sin",
        label: "Sine to Power n",
        type: "trig_power_n",
        inputs: ["power", "argument"],
      },
      {
        symbol: "cosⁿ",
        latex: "\\cos",
        label: "Cosine to Power n",
        type: "trig_power_n",
        inputs: ["power", "argument"],
      },
      {
        symbol: "tanⁿ",
        latex: "\\tan",
        label: "Tangent to Power n",
        type: "trig_power_n",
        inputs: ["power", "argument"],
      },
      { symbol: "ln", latex: "\\ln", label: "Natural Log", type: "function", inputs: ["argument"] },
      { symbol: "log", latex: "\\log", label: "Logarithm", type: "function", inputs: ["argument"] },
    ],
    Matrix: [
      {
        symbol: "[ ]",
        latex: "\\begin{bmatrix} a & b \\\\ c & d \\end{bmatrix}",
        label: "Matrix",
        type: "matrix",
        inputs: ["rows", "cols"],
      },
      {
        symbol: "| |",
        latex: "\\begin{vmatrix} a & b \\\\ c & d \\end{vmatrix}",
        label: "Determinant",
        type: "determinant",
        inputs: ["rows", "cols"],
      },
      {
        symbol: "( )",
        latex: "\\begin{pmatrix} a & b \\\\ c & d \\end{pmatrix}",
        label: "Parentheses Matrix",
        type: "pmatrix",
        inputs: ["rows", "cols"],
      },
    ],
    Inequalities: [
      { symbol: "≤", latex: "\\leq", label: "Less or Equal", type: "direct" },
      { symbol: "≥", latex: "\\geq", label: "Greater or Equal", type: "direct" },
      { symbol: "≠", latex: "\\neq", label: "Not Equal", type: "direct" },
      { symbol: "≈", latex: "\\approx", label: "Approximately", type: "direct" },
      { symbol: "|x|", latex: "|x|", label: "Absolute Value", type: "absolute", inputs: ["expression"] },
      {
        symbol: "a<b≤c",
        latex: "a < b \\leq c",
        label: "Inequality Chain",
        type: "inequality_chain",
        inputs: ["left", "middle", "right", "operator1", "operator2"],
      },
    ],
    Complex: [
      { symbol: "i", latex: "i", label: "Imaginary Unit", type: "direct" },
      { symbol: "a+bi", latex: "a + bi", label: "Complex Number", type: "complex", inputs: ["real", "imaginary"] },
      {
        symbol: "r∠θ",
        latex: "r(\\cos\\theta + i\\sin\\theta)",
        label: "Polar Form",
        type: "polar",
        inputs: ["radius", "angle"],
      },
      { symbol: "z*", latex: "\\overline{z}", label: "Complex Conjugate", type: "conjugate", inputs: ["complex"] },
      { symbol: "|z|", latex: "|z|", label: "Complex Modulus", type: "modulus", inputs: ["complex"] },
    ],
    Vectors: [
      { symbol: "→a", latex: "\\vec{a}", label: "Vector", type: "vector", inputs: ["variable"] },
      {
        symbol: "a·b",
        latex: "\\vec{a} \\cdot \\vec{b}",
        label: "Dot Product",
        type: "dot_product",
        inputs: ["vector1", "vector2"],
      },
      {
        symbol: "a×b",
        latex: "\\vec{a} \\times \\vec{b}",
        label: "Cross Product",
        type: "cross_product",
        inputs: ["vector1", "vector2"],
      },
      {
        symbol: "⟨a,b,c⟩",
        latex: "\\langle a, b, c \\rangle",
        label: "Vector Components",
        type: "vector_components",
        inputs: ["x", "y", "z"],
      },
      {
        symbol: "||a||",
        latex: "||\\vec{a}||",
        label: "Vector Magnitude",
        type: "vector_magnitude",
        inputs: ["vector"],
      },
    ],
    Logic: [
      { symbol: "∪", latex: "\\cup", label: "Union", type: "direct" },
      { symbol: "∩", latex: "\\cap", label: "Intersection", type: "direct" },
      { symbol: "⊂", latex: "\\subset", label: "Subset", type: "direct" },
      { symbol: "∈", latex: "\\in", label: "Element Of", type: "direct" },
      { symbol: "∉", latex: "\\notin", label: "Not Element Of", type: "direct" },
      { symbol: "⇒", latex: "\\Rightarrow", label: "Implies", type: "direct" },
      { symbol: "⇔", latex: "\\Leftrightarrow", label: "If and Only If", type: "direct" },
      { symbol: "¬", latex: "\\neg", label: "Not", type: "direct" },
      { symbol: "∀", latex: "\\forall", label: "For All", type: "direct" },
      { symbol: "∃", latex: "\\exists", label: "There Exists", type: "direct" },
    ],
    Advanced: [
      {
        symbol: "f(x)={",
        latex: "f(x) = \\begin{cases}",
        label: "Piecewise Function",
        type: "piecewise",
        inputs: ["variable", "pieces"],
      },
      {
        symbol: "lim→∞",
        latex: "\\lim_{x \\to \\infty}",
        label: "Limit to Infinity",
        type: "limit_infinity",
        inputs: ["variable", "function"],
      },
      {
        symbol: "lim→0⁺",
        latex: "\\lim_{x \\to 0^+}",
        label: "Right Limit",
        type: "limit_right",
        inputs: ["variable", "approaches", "function"],
      },
      {
        symbol: "lim→0⁻",
        latex: "\\lim_{x \\to 0^-}",
        label: "Left Limit",
        type: "limit_left",
        inputs: ["variable", "approaches", "function"],
      },
      { symbol: "∞", latex: "\\infty", label: "Infinity", type: "direct" },
      {
        symbol: "∂²/∂x²",
        latex: "\\frac{\\partial^2}{\\partial x^2}",
        label: "Second Partial",
        type: "second_partial",
        inputs: ["function", "variable"],
      },
      {
        symbol: "sin⁻¹",
        latex: "\\sin",
        label: "Inverse Sine",
        type: "trig_power_n",
        inputs: ["power", "argument"],
        defaultPower: "-1",
      },
      {
        symbol: "cos⁻¹",
        latex: "\\cos",
        label: "Inverse Cosine",
        type: "trig_power_n",
        inputs: ["power", "argument"],
        defaultPower: "-1",
      },
      {
        symbol: "tan⁻¹",
        latex: "\\tan",
        label: "Inverse Tangent",
        type: "trig_power_n",
        inputs: ["power", "argument"],
        defaultPower: "-1",
      },
      { symbol: "sinh", latex: "\\sinh", label: "Hyperbolic Sine", type: "function", inputs: ["argument"] },
      { symbol: "cosh", latex: "\\cosh", label: "Hyperbolic Cosine", type: "function", inputs: ["argument"] },
      { symbol: "tanh", latex: "\\tanh", label: "Hyperbolic Tangent", type: "function", inputs: ["argument"] },
    ],
  }
  const handleSymbolClick = (item) => {
    if (item.type === "direct") {
      // Direct insertion for simple symbols, wrapped in $ for inline math
      onInsert(`$${item.latex}$`)
    } else {
      // Open input dialog for complex expressions
      setCurrentMathType(item)
      setInputValues(item.defaultPower ? { power: item.defaultPower } : {})
      setPreviewLatex("")
      setShowInputDialog(true)
    }
  }
  const handleInputChange = (inputName, value) => {
    const newInputValues = { ...inputValues, [inputName]: value }
    setInputValues(newInputValues)
    // Generate preview based on math type
    const latex = generateLatex(currentMathType, newInputValues)
    setPreviewLatex(latex)
  }
  const generateLatex = (mathType, values) => {
    if (!mathType || !values) return ""
    switch (mathType.type) {
      case "fraction":
        return values.numerator && values.denominator ? `\\frac{${values.numerator}}{${values.denominator}}` : ""
      case "fraction_power":
        return values.numerator && values.denominator && values.power
          ? `\\frac{${values.numerator}}{${values.denominator}^{${values.power}}}`
          : ""
      case "power":
        if (mathType.label === "Square") {
          return values.base ? `${values.base}^2` : ""
        } else if (mathType.label === "Cube") {
          return values.base ? `${values.base}^3` : ""
        } else {
          return values.base && values.exponent ? `${values.base}^{${values.exponent}}` : ""
        }
      case "root":
        if (mathType.label === "Square Root") {
          return values.value ? `\\sqrt{${values.value}}` : ""
        } else if (mathType.label === "Cube Root") {
          return values.value ? `\\sqrt[3]{${values.value}}` : ""
        } else {
          return values.index && values.value ? `\\sqrt[${values.index}]{${values.value}}` : ""
        }
      case "function":
        return values.argument ? `\\${mathType.latex.replace("\\", "")}(${values.argument})` : ""
      case "trig_power_n":
        return values.power && values.argument
          ? `\\${mathType.latex.replace("\\", "")}^{${values.power}}(${values.argument})`
          : ""
      case "integral":
        return values.function ? `\\int ${values.function} \\, dx` : ""
      case "definite_integral":
        return values.lower && values.upper && values.function
          ? `\\int_{${values.lower}}^{${values.upper}} ${values.function} \\, dx`
          : ""
      case "derivative":
        return values.function ? `\\frac{d}{dx}(${values.function})` : ""
      case "partial":
        return values.function && values.variable
          ? `\\frac{\\partial}{\\partial ${values.variable}}(${values.function})`
          : ""
      case "sum":
        return values.expression ? `\\sum ${values.expression}` : ""
      case "sum_limits":
        return values.variable && values.lower && values.upper && values.expression
          ? `\\sum_{${values.variable}=${values.lower}}^{${values.upper}} ${values.expression}`
          : ""
      case "product":
        return values.expression ? `\\prod ${values.expression}` : ""
      case "limit":
        return values.variable && values.approaches && values.function
          ? `\\lim_{${values.variable} \\to ${values.approaches}} ${values.function}`
          : ""
      case "matrix":
      case "determinant":
      case "pmatrix":
        return generateMatrixLatex(mathType.type, values)
      case "absolute":
        return values.expression ? `|${values.expression}|` : ""
      case "inequality_chain":
        return values.left && values.middle && values.right && values.operator1 && values.operator2
          ? `${values.left} ${values.operator1} ${values.middle} ${values.operator2} ${values.right}`
          : ""
      case "complex":
        return values.real && values.imaginary ? `${values.real} + ${values.imaginary}i` : ""
      case "polar":
        return values.radius && values.angle ? `${values.radius}(\\cos ${values.angle} + i\\sin ${values.angle})` : ""
      case "conjugate":
        return values.complex ? `\\overline{${values.complex}}` : ""
      case "modulus":
        return values.complex ? `|${values.complex}|` : ""
      case "vector":
        return values.variable ? `\\vec{${values.variable}}` : ""
      case "dot_product":
        return values.vector1 && values.vector2 ? `\\vec{${values.vector1}} \\cdot \\vec{${values.vector2}}` : ""
      case "cross_product":
        return values.vector1 && values.vector2 ? `\\vec{${values.vector1}} \\times \\vec{${values.vector2}}` : ""
      case "vector_components":
        return values.x && values.y
          ? values.z
            ? `\\langle ${values.x}, ${values.y}, ${values.z} \\rangle`
            : `\\langle ${values.x}, ${values.y} \\rangle`
          : ""
      case "vector_magnitude":
        return values.vector ? `||\\vec{${values.vector}}||` : ""
      case "piecewise":
        return generatePiecewiseLatex(values)
      case "limit_infinity":
        return values.variable && values.function ? `\\lim_{${values.variable} \\to \\infty} ${values.function}` : ""
      case "limit_right":
        return values.variable && values.approaches && values.function
          ? `\\lim_{${values.variable} \\to ${values.approaches}^+} ${values.function}`
          : ""
      case "limit_left":
        return values.variable && values.approaches && values.function
          ? `\\lim_{${values.variable} \\to ${values.approaches}^-} ${values.function}`
          : ""
      case "second_partial":
        return values.function && values.variable
          ? `\\frac{\\partial^2}{\\partial ${values.variable}^2}(${values.function})`
          : ""
      default:
        return ""
    }
  }
  const generateMatrixLatex = (matrixType, values) => {
    const rows = Number.parseInt(values.rows) || 2
    const cols = Number.parseInt(values.cols) || 2
    if (rows < 1 || cols < 1 || rows > 5 || cols > 5) return ""
    const matrixStart = {
      matrix: "\\begin{bmatrix}",
      determinant: "\\begin{vmatrix}",
      pmatrix: "\\begin{pmatrix}",
    }
    const matrixEnd = {
      matrix: "\\end{bmatrix}",
      determinant: "\\end{vmatrix}",
      pmatrix: "\\end{pmatrix}",
    }
    let matrixContent = ""
    for (let i = 0; i < rows; i++) {
      const rowElements = []
      for (let j = 0; j < cols; j++) {
        const cellValue = values[`cell_${i}_${j}`] || `a_${i + 1}${j + 1}`
        rowElements.push(cellValue)
      }
      matrixContent += rowElements.join(" & ")
      if (i < rows - 1) matrixContent += " \\\\ "
    }
    return `${matrixStart[matrixType]} ${matrixContent} ${matrixEnd[matrixType]}`
  }
  const generatePiecewiseLatex = (values) => {
    if (!values.variable || !values.pieces) return ""
    const pieces = values.pieces || 1
    let piecewiseContent = ""
    for (let i = 0; i < pieces; i++) {
      const expression = values[`piece_${i}_expr`] || `f_${i}(x)`
      const condition = values[`piece_${i}_cond`] || `condition_${i}`
      piecewiseContent += `${expression} & \\text{if } ${condition}`
      if (i < pieces - 1) piecewiseContent += " \\\\ "
    }
    return `${values.variable} = \\begin{cases} ${piecewiseContent} \\end{cases}`
  }
  const insertExpression = () => {
    if (previewLatex) {
      onInsert(`$${previewLatex}$`) // Wrap with $ for inline math
      setShowInputDialog(false)
    }
  }
  // Safe KaTeX rendering function
  const renderKaTeX = (latex) => {
    try {
      return <InlineMath math={latex} />
    } catch (error) {
      console.error("KaTeX rendering error:", error)
      return <span style={{ color: "red" }}>[Math Error: {latex}]</span>
    }
  }
  const renderInputDialog = () => {
    if (!currentMathType || !showInputDialog) return null
    const inputStyle = {
      padding: "8px 12px",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      fontSize: "14px",
    }
    return (
      <>
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            zIndex: 1001,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              width: "500px",
              maxHeight: "80vh",
              overflow: "auto",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600" }}>Create {currentMathType.label}</h3>
              <button
                onClick={() => setShowInputDialog(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#6b7280",
                }}
              >
                ×
              </button>
            </div>
            {/* Input Fields */}
            <div style={{ marginBottom: "20px" }}>
              {currentMathType.type === "matrix" ||
              currentMathType.type === "determinant" ||
              currentMathType.type === "pmatrix" ? (
                <div>
                  {/* Matrix Size Input */}
                  <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: "block", fontSize: "14px", marginBottom: "4px" }}>Rows:</label>
                      <input
                        type="number"
                        min="1"
                        max="5"
                        value={inputValues.rows || ""}
                        onChange={(e) => handleInputChange("rows", e.target.value)}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          fontSize: "14px",
                        }}
                        placeholder="2"
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: "block", fontSize: "14px", marginBottom: "4px" }}>Columns:</label>
                      <input
                        type="number"
                        min="1"
                        max="5"
                        value={inputValues.cols || ""}
                        onChange={(e) => handleInputChange("cols", e.target.value)}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          fontSize: "14px",
                        }}
                        placeholder="2"
                      />
                    </div>
                  </div>
                  {/* Matrix Elements with Power Support */}
                  {inputValues.rows && inputValues.cols && (
                    <div>
                      <label style={{ display: "block", fontSize: "14px", marginBottom: "8px" }}>
                        Matrix Elements (you can use powers like x^2, a^2):
                      </label>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: `repeat(${inputValues.cols}, 1fr)`,
                          gap: "8px",
                          marginBottom: "16px",
                        }}
                      >
                        {Array.from({
                          length: Number.parseInt(inputValues.rows) * Number.parseInt(inputValues.cols),
                        }).map((_, index) => {
                          const row = Math.floor(index / Number.parseInt(inputValues.cols))
                          const col = index % Number.parseInt(inputValues.cols)
                          return (
                            <div key={`cell_${row}_${col}`} style={{ position: "relative" }}>
                              <input
                                type="text"
                                value={inputValues[`cell_${row}_${col}`] || ""}
                                onChange={(e) => handleInputChange(`cell_${row}_${col}`, e.target.value)}
                                style={{
                                  width: "100%",
                                  padding: "6px 25px 6px 8px",
                                  border: "1px solid #d1d5db",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                  textAlign: "center",
                                }}
                                placeholder={`a_${row + 1}${col + 1}`}
                              />
                              <button
                                type="button"
                                onClick={() => addPowerToMatrixCell(row, col)}
                                style={{
                                  position: "absolute",
                                  right: "2px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  background: "#a855f7",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "2px",
                                  width: "16px",
                                  height: "16px",
                                  cursor: "pointer",
                                  fontSize: "8px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                                title="Add Power"
                              >
                                ²
                              </button>
                            </div>
                          )
                        })}
                      </div>
                      {/* Quick Matrix Functions */}
                      <div style={{ display: "flex", gap: "6px", marginBottom: "12px", flexWrap: "wrap" }}>
                        <button
                          type="button"
                          onClick={() => fillMatrixPattern("identity")}
                          style={{
                            padding: "4px 8px",
                            border: "1px solid #e5e7eb",
                            borderRadius: "4px",
                            backgroundColor: "white",
                            cursor: "pointer",
                            fontSize: "11px",
                          }}
                        >
                          Identity Matrix
                        </button>
                        <button
                          type="button"
                          onClick={() => fillMatrixPattern("zero")}
                          style={{
                            padding: "4px 8px",
                            border: "1px solid #e5e7eb",
                            borderRadius: "4px",
                            backgroundColor: "white",
                            cursor: "pointer",
                            fontSize: "11px",
                          }}
                        >
                          Zero Matrix
                        </button>
                        <button
                          type="button"
                          onClick={() => fillMatrixPattern("variables")}
                          style={{
                            padding: "4px 8px",
                            border: "1px solid #e5e7eb",
                            borderRadius: "4px",
                            backgroundColor: "white",
                            cursor: "pointer",
                            fontSize: "11px",
                          }}
                        >
                          Variable Matrix
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : currentMathType.type === "inequality_chain" ? (
                <div>
                  <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                    <input
                      type="text"
                      value={inputValues.left || ""}
                      onChange={(e) => handleInputChange("left", e.target.value)}
                      placeholder="a"
                      style={inputStyle}
                    />
                    <select
                      value={inputValues.operator1 || ""}
                      onChange={(e) => handleInputChange("operator1", e.target.value)}
                      style={inputStyle}
                    >
                      <option value="">Op1</option>
                      <option value="<">{"<"}</option>
                      <option value="\\leq">{"≤"}</option>
                      <option value=">">{">"}</option>
                      <option value="\\geq">{"≥"}</option>
                    </select>
                    <input
                      type="text"
                      value={inputValues.middle || ""}
                      onChange={(e) => handleInputChange("middle", e.target.value)}
                      placeholder="b"
                      style={inputStyle}
                    />
                    <select
                      value={inputValues.operator2 || ""}
                      onChange={(e) => handleInputChange("operator2", e.target.value)}
                      style={inputStyle}
                    >
                      <option value="">Op2</option>
                      <option value="<">{"<"}</option>
                      <option value="\\leq">{"≤"}</option>
                      <option value=">">{">"}</option>
                      <option value="\\geq">{"≥"}</option>
                    </select>
                    <input
                      type="text"
                      value={inputValues.right || ""}
                      onChange={(e) => handleInputChange("right", e.target.value)}
                      placeholder="c"
                      style={inputStyle}
                    />
                  </div>
                </div>
              ) : currentMathType.type === "piecewise" ? (
                <div>
                  <div style={{ marginBottom: "12px" }}>
                    <label style={{ display: "block", fontSize: "14px", marginBottom: "4px" }}>
                      Function Variable:
                    </label>
                    <input
                      type="text"
                      value={inputValues.variable || ""}
                      onChange={(e) => handleInputChange("variable", e.target.value)}
                      placeholder="f(x)"
                      style={inputStyle}
                    />
                  </div>
                  <div style={{ marginBottom: "12px" }}>
                    <label style={{ display: "block", fontSize: "14px", marginBottom: "4px" }}>Number of Pieces:</label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      value={inputValues.pieces || ""}
                      onChange={(e) => handleInputChange("pieces", e.target.value)}
                      placeholder="2"
                      style={inputStyle}
                    />
                  </div>
                  {inputValues.pieces &&
                    Array.from({ length: Number.parseInt(inputValues.pieces) }).map((_, i) => (
                      <div
                        key={i}
                        style={{
                          marginBottom: "12px",
                          padding: "12px",
                          border: "1px solid #e5e7eb",
                          borderRadius: "6px",
                        }}
                      >
                        <div style={{ fontSize: "12px", fontWeight: "500", marginBottom: "8px" }}>Piece {i + 1}:</div>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <input
                            type="text"
                            value={inputValues[`piece_${i}_expr`] || ""}
                            onChange={(e) => handleInputChange(`piece_${i}_expr`, e.target.value)}
                            placeholder="Expression"
                            style={{ ...inputStyle, flex: 1 }}
                          />
                          <input
                            type="text"
                            value={inputValues[`piece_${i}_cond`] || ""}
                            onChange={(e) => handleInputChange(`piece_${i}_cond`, e.target.value)}
                            placeholder="Condition"
                            style={{ ...inputStyle, flex: 1 }}
                          />
                        </div>
                      </div>
                    ))}
                </div>
              ) : currentMathType.type === "vector_components" ? (
                <div>
                  <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                    <input
                      type="text"
                      value={inputValues.x || ""}
                      onChange={(e) => handleInputChange("x", e.target.value)}
                      placeholder="x component"
                      style={inputStyle}
                    />
                    <input
                      type="text"
                      value={inputValues.y || ""}
                      onChange={(e) => handleInputChange("y", e.target.value)}
                      placeholder="y component"
                      style={inputStyle}
                    />
                    <input
                      type="text"
                      value={inputValues.z || ""}
                      onChange={(e) => handleInputChange("z", e.target.value)}
                      placeholder="z component (optional)"
                      style={inputStyle}
                    />
                  </div>
                </div>
              ) : (
                // Regular input fields
                currentMathType.inputs?.map((inputName) => (
                  <div key={inputName} style={{ marginBottom: "12px" }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "500",
                        marginBottom: "4px",
                        textTransform: "capitalize",
                      }}
                    >
                      {inputName}:
                    </label>
                    <div style={{ position: "relative" }}>
                      <input
                        type="text"
                        value={inputValues[inputName] || ""}
                        onChange={(e) => handleInputChange(inputName, e.target.value)}
                        style={{
                          width: "100%",
                          padding: "8px 40px 8px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          fontSize: "14px",
                        }}
                        placeholder={getPlaceholder(inputName, currentMathType.type)}
                      />
                      {/* Power/Expression Builder Button */}
                      <button
                        type="button"
                        onClick={() => openExpressionBuilder(inputName)}
                        style={{
                          position: "absolute",
                          right: "8px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "#a855f7",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          width: "24px",
                          height: "24px",
                          cursor: "pointer",
                          fontSize: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        title="Add Power/Expression"
                      >
                        x²
                      </button>
                    </div>
                    {/* Enhanced Quick Power Buttons with Trig Functions */}
                    <div style={{ display: "flex", gap: "4px", marginTop: "4px", flexWrap: "wrap" }}>
                      <button type="button" onClick={() => addPowerToInput(inputName, "^2")} style={quickButtonStyle}>
                        x²
                      </button>
                      <button type="button" onClick={() => addPowerToInput(inputName, "^3")} style={quickButtonStyle}>
                        x³
                      </button>
                      <button type="button" onClick={() => addPowerToInput(inputName, "^{n}")} style={quickButtonStyle}>
                        xⁿ
                      </button>
                      <button
                        type="button"
                        onClick={() => addTrigPowerToInput(inputName, "sin")}
                        style={quickButtonStyle}
                      >
                        sinⁿ
                      </button>
                      <button
                        type="button"
                        onClick={() => addTrigPowerToInput(inputName, "cos")}
                        style={quickButtonStyle}
                      >
                        cosⁿ
                      </button>
                      <button
                        type="button"
                        onClick={() => addTrigPowerToInput(inputName, "tan")}
                        style={quickButtonStyle}
                      >
                        tanⁿ
                      </button>
                      <button
                        type="button"
                        onClick={() => addFunctionToInput(inputName, "ln")}
                        style={quickButtonStyle}
                      >
                        ln
                      </button>
                      <button
                        type="button"
                        onClick={() => addTrigPowerToInput(inputName, "log")}
                        style={quickButtonStyle}
                      >
                        logⁿ
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            {/* Preview */}
            {previewLatex && (
              <div
                style={{
                  padding: "16px",
                  backgroundColor: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  marginBottom: "20px",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "8px" }}>Preview:</div>
                <div style={{ fontSize: "18px" }}>{renderKaTeX(previewLatex)}</div>
              </div>
            )}
            {/* Action Buttons */}
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowInputDialog(false)}
                style={{
                  padding: "10px 20px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  backgroundColor: "white",
                  color: "#374151",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={insertExpression}
                disabled={!previewLatex}
                style={{
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "6px",
                  backgroundColor: previewLatex ? "#a855f7" : "#9ca3af",
                  color: "white",
                  cursor: previewLatex ? "pointer" : "not-allowed",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Insert Expression
              </button>
            </div>
          </div>
        </div>
        {/* Trig Power Dialog */}
        {showTrigPowerDialog && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              zIndex: 1002,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
                padding: "24px",
                width: "400px",
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
                border: "2px solid #a855f7",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600", color: "#a855f7" }}>
                  Add {trigPowerData.func}^n Function
                </h3>
                <button
                  onClick={() => setShowTrigPowerDialog(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "24px",
                    cursor: "pointer",
                    color: "#6b7280",
                  }}
                >
                  ×
                </button>
              </div>
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "6px",
                    color: "#374151",
                  }}
                >
                  Power (n):
                </label>
                <input
                  type="text"
                  value={trigPowerData.power}
                  onChange={(e) => setTrigPowerData({ ...trigPowerData, power: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "2px solid #e5e7eb",
                    borderRadius: "8px",
                    fontSize: "14px",
                    outline: "none",
                    transition: "border-color 0.2s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#a855f7")}
                  onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                  placeholder="e.g., 2, 3, 4, n, -1"
                />
                <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                  Examples: 2 (for sin²), 3 (for sin³), -1 (for sin⁻¹), n (for sinⁿ)
                </div>
              </div>
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "6px",
                    color: "#374151",
                  }}
                >
                  Argument:
                </label>
                <input
                  type="text"
                  value={trigPowerData.argument}
                  onChange={(e) => setTrigPowerData({ ...trigPowerData, argument: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "2px solid #e5e7eb",
                    borderRadius: "8px",
                    fontSize: "14px",
                    outline: "none",
                    transition: "border-color 0.2s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#a855f7")}
                  onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                  placeholder="e.g., x, 2x+1, θ, π/4"
                />
                <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                  Examples: x, 2x+1, θ, π/4, (x²+1)
                </div>
              </div>
              {/* Preview */}
              {trigPowerData.power && trigPowerData.argument && (
                <div
                  style={{
                    padding: "12px",
                    backgroundColor: "#f8fafc",
                    border: "2px solid #e879f9",
                    borderRadius: "8px",
                    marginBottom: "20px",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: "12px", color: "#a855f7", marginBottom: "6px", fontWeight: "500" }}>
                    Preview:
                  </div>
                  <div style={{ fontSize: "20px", fontFamily: "serif", color: "#1f2937" }}>
                    {trigPowerData.func}
                    <sup>{trigPowerData.power}</sup>({trigPowerData.argument})
                  </div>
                </div>
              )}
              {/* Quick Power Buttons */}
              <div style={{ marginBottom: "20px" }}>
                <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "8px", fontWeight: "500" }}>
                  Quick Powers:
                </div>
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  {["1", "2", "3", "4", "-1", "n"].map((power) => (
                    <button
                      key={power}
                      onClick={() => setTrigPowerData({ ...trigPowerData, power })}
                      style={{
                        padding: "6px 12px",
                        border: trigPowerData.power === power ? "2px solid #a855f7" : "1px solid #e5e7eb",
                        borderRadius: "6px",
                        backgroundColor: trigPowerData.power === power ? "#f3e8ff" : "white",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "500",
                        color: trigPowerData.power === power ? "#a855f7" : "#6b7280",
                        transition: "all 0.2s",
                      }}
                    >
                      {power === "-1" ? "⁻¹" : power === "n" ? "ⁿ" : power}
                    </button>
                  ))}
                </div>
              </div>
              {/* Action Buttons */}
              <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
                <button
                  onClick={() => setShowTrigPowerDialog(false)}
                  style={{
                    padding: "10px 20px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    backgroundColor: "white",
                    color: "#374151",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={insertTrigPower}
                  disabled={!trigPowerData.power || !trigPowerData.argument}
                  style={{
                    padding: "10px 20px",
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor: trigPowerData.power && trigPowerData.argument ? "#a855f7" : "#9ca3af",
                    color: "white",
                    cursor: trigPowerData.power && trigPowerData.argument ? "pointer" : "not-allowed",
                    fontSize: "14px",
                    fontWeight: "500",
                    transition: "background-color 0.2s",
                  }}
                >
                  Insert {trigPowerData.func}^{trigPowerData.power}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    )
  }
  const quickButtonStyle = {
    padding: "4px 8px",
    border: "1px solid #e5e7eb",
    borderRadius: "4px",
    backgroundColor: "white",
    cursor: "pointer",
    fontSize: "11px",
  }
  const addPowerToInput = (inputName, power) => {
    setInputValues((prev) => ({
      ...prev,
      [inputName]: (prev[inputName] || "") + power,
    }))
    setPreviewLatex(
      generateLatex(currentMathType, { ...inputValues, [inputName]: (inputValues[inputName] || "") + power }),
    )
  }
  const addTrigPowerToInput = (inputName, func) => {
    setTrigPowerData({ func: func, inputName: inputName, power: "1", argument: "" })
    setShowTrigPowerDialog(true)
  }
  const addFunctionToInput = (inputName, func) => {
    setInputValues((prev) => ({
      ...prev,
      [inputName]: func + "(" + (prev[inputName] || "") + ")",
    }))
    setPreviewLatex(
      generateLatex(currentMathType, {
        ...inputValues,
        [inputName]: func + "(" + (inputValues[inputName] || "") + ")",
      }),
    )
  }
  const insertTrigPower = () => {
    const { func, power, argument, inputName } = trigPowerData
    const trigExpression = `\\${func}^{${power}}(${argument})`
    // Wrap the inserted expression with $ for inline math
    onInsert(`$${trigExpression}$`)
    setShowTrigPowerDialog(false)
  }
  const openExpressionBuilder = (inputName) => {
    // For now, show a simple power dialog
    const power = prompt("Enter power (e.g., 2, n, x+1):")
    if (power) {
      addPowerToInput(inputName, `^{${power}}`)
    }
  }
  const getPlaceholder = (inputName, mathType) => {
    const placeholders = {
      function: "e.g., x^2, sin(x), x^2 + 1",
      argument: "e.g., x, x^2, 2x+1",
      numerator: "e.g., x, x^2, a+b",
      denominator: "e.g., y, y^2, c+d",
      base: "e.g., x, a, 2x",
      exponent: "e.g., 2, n, x+1",
      expression: "e.g., i^2, x^n, a_i",
      power: "e.g., 2, 3, n, -1",
    }
    return placeholders[inputName] || `Enter ${inputName}`
  }
  const addPowerToMatrixCell = (row, col) => {
    const power = prompt("Enter power (e.g., 2, n, i+j):")
    if (power) {
      const cellKey = `cell_${row}_${col}`
      const currentValue = inputValues[cellKey] || ""
      const newValue = currentValue + `^{${power}}`
      handleInputChange(cellKey, newValue)
    }
  }
  const fillMatrixPattern = (pattern) => {
    const rows = Number.parseInt(inputValues.rows) || 2
    const cols = Number.parseInt(inputValues.cols) || 2
    const newValues = { ...inputValues }
    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < cols; j++) {
        const cellKey = `cell_${i}_${j}`
        switch (pattern) {
          case "identity":
            newValues[cellKey] = i === j ? "1" : "0"
            break
          case "zero":
            newValues[cellKey] = "0"
            break
          case "variables":
            newValues[cellKey] = `a_${i + 1}${j + 1}`
            break
        }
      }
    }
    setInputValues(newValues)
    const latex = generateLatex(currentMathType, newValues)
    setPreviewLatex(latex)
  }
  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 999,
        }}
        onClick={onClose}
      />
      {/* Main Math Expression Builder */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "white",
          border: "1px solid #d1d5db",
          borderRadius: "12px",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
          zIndex: 1000,
          width: "800px",
          maxHeight: "80vh",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#f8fafc",
          }}
        >
          <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "600" }}>
            Smart Math Expression Builder - {fieldType}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              color: "#6b7280",
            }}
          >
            ×
          </button>
        </div>
        {/* Category Tabs */}
        <div
          style={{
            display: "flex",
            overflowX: "auto",
            borderBottom: "1px solid #e5e7eb",
            backgroundColor: "#f9fafb",
          }}
        >
          {Object.keys(mathCategories).map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              style={{
                padding: "8px 16px",
                border: "none",
                backgroundColor: activeCategory === category ? "#a855f7" : "transparent",
                color: activeCategory === category ? "white" : "#6b7280",
                fontSize: "12px",
                fontWeight: "500",
                cursor: "pointer",
                whiteSpace: "nowrap",
                borderRadius: "4px",
                margin: "4px",
              }}
            >
              {category}
            </button>
          ))}
        </div>
        {/* Symbols Grid */}
        <div
          style={{
            padding: "16px",
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: "8px",
            maxHeight: "400px",
            overflowY: "auto",
          }}
        >
          {mathCategories[activeCategory]?.map((item, index) => (
            <button
              key={index}
              onClick={() => handleSymbolClick(item)}
              title={item.label}
              style={{
                padding: "12px 8px",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                backgroundColor: "white",
                cursor: "pointer",
                fontSize: "16px",
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "60px",
                transition: "all 0.2s",
                flexDirection: "column",
                gap: "4px",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f3f4f6"
                e.target.style.borderColor = "#a855f7"
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "white"
                e.target.style.borderColor = "#e5e7eb"
              }}
            >
              <div>{item.symbol}</div>
              {item.type !== "direct" && <div style={{ fontSize: "10px", color: "#6b7280" }}>Smart Input</div>}
            </button>
          ))}
        </div>
        {/* Instructions */}
        <div
          style={{
            padding: "12px 16px",
            backgroundColor: "#f8fafc",
            borderTop: "1px solid #e5e7eb",
            fontSize: "12px",
            color: "#6b7280",
          }}
        >
          <strong>Smart Input:</strong> Click on symbols with "Smart Input" to get guided dialogs. Direct symbols insert
          immediately.
        </div>
      </div>
      {/* Input Dialog */}
      {renderInputDialog()}
      {/* Trig Power Dialog */}
      {showTrigPowerDialog && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            zIndex: 1002,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              width: "400px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
              border: "2px solid #a855f7",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600", color: "#a855f7" }}>
                Add {trigPowerData.func}^n Function
              </h3>
              <button
                onClick={() => setShowTrigPowerDialog(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#6b7280",
                }}
              >
                ×
              </button>
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "6px",
                  color: "#374151",
                }}
              >
                Power (n):
              </label>
              <input
                type="text"
                value={trigPowerData.power}
                onChange={(e) => setTrigPowerData({ ...trigPowerData, power: e.target.value })}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "2px solid #e5e7eb",
                  borderRadius: "8px",
                  fontSize: "14px",
                  outline: "none",
                  transition: "border-color 0.2s",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#a855f7")}
                onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                placeholder="e.g., 2, 3, 4, n, -1"
              />
              <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                Examples: 2 (for sin²), 3 (for sin³), -1 (for sin⁻¹), n (for sinⁿ)
              </div>
            </div>
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "6px",
                  color: "#374151",
                }}
              >
                Argument:
              </label>
              <input
                type="text"
                value={trigPowerData.argument}
                onChange={(e) => setTrigPowerData({ ...trigPowerData, argument: e.target.value })}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "2px solid #e5e7eb",
                  borderRadius: "8px",
                  fontSize: "14px",
                  outline: "none",
                  transition: "border-color 0.2s",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#a855f7")}
                onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                placeholder="e.g., x, 2x+1, θ, π/4"
              />
              <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                Examples: x, 2x+1, θ, π/4, (x²+1)
              </div>
            </div>
            {/* Preview */}
            {trigPowerData.power && trigPowerData.argument && (
              <div
                style={{
                  padding: "12px",
                  backgroundColor: "#f8fafc",
                  border: "2px solid #e879f9",
                  borderRadius: "8px",
                  marginBottom: "20px",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: "12px", color: "#a855f7", marginBottom: "6px", fontWeight: "500" }}>
                  Preview:
                </div>
                <div style={{ fontSize: "20px", fontFamily: "serif", color: "#1f2937" }}>
                  {trigPowerData.func}
                  <sup>{trigPowerData.power}</sup>({trigPowerData.argument})
                </div>
              </div>
            )}
            {/* Quick Power Buttons */}
            <div style={{ marginBottom: "20px" }}>
              <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "8px", fontWeight: "500" }}>
                Quick Powers:
              </div>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {["1", "2", "3", "4", "-1", "n"].map((power) => (
                  <button
                    key={power}
                    onClick={() => setTrigPowerData({ ...trigPowerData, power })}
                    style={{
                      padding: "6px 12px",
                      border: trigPowerData.power === power ? "2px solid #a855f7" : "1px solid #e5e7eb",
                      borderRadius: "6px",
                      backgroundColor: trigPowerData.power === power ? "#f3e8ff" : "white",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: "500",
                      color: trigPowerData.power === power ? "#a855f7" : "#6b7280",
                      transition: "all 0.2s",
                    }}
                  >
                    {power === "-1" ? "⁻¹" : power === "n" ? "ⁿ" : power}
                  </button>
                ))}
              </div>
            </div>
            {/* Action Buttons */}
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowTrigPowerDialog(false)}
                style={{
                  padding: "10px 20px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  backgroundColor: "white",
                  color: "#374151",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Cancel
              </button>
              <button
                onClick={insertTrigPower}
                disabled={!trigPowerData.power || !trigPowerData.argument}
                style={{
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "8px",
                  backgroundColor: trigPowerData.power && trigPowerData.argument ? "#a855f7" : "#9ca3af",
                  color: "white",
                  cursor: trigPowerData.power && trigPowerData.argument ? "pointer" : "not-allowed",
                  fontSize: "14px",
                  fontWeight: "500",
                  transition: "background-color 0.2s",
                }}
              >
                Insert {trigPowerData.func}^{trigPowerData.power}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
